import dayjs from "dayjs";
import { useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../redux/store";
import { updateUser } from "../redux/Reducers/userAuthReducer";
import { clearGroupInfo } from "../redux/Reducers/gcGroupReducer";
import { T, TF } from "react-translator-component";
import authService from "../services/app-services/auth-service";
import appServices from "../services/app-services/appServices";
import setAuthorizationToken from "../services/axios-util/set-authorization-token";
import { getRole, isRegularAppUser, convertUserImage, isGroupLeader } from "../util/utils";
import { handleClickUrl } from "../util/PortalAppFunction";
import LoadingModal from "../components/LoadingModal";
import EventCalendar from "../components/GreenCity/EventCalender";
import ButtonArrow from "../components/GreenCity/ButtonArrow";
import GroupIconImage from "../components/GreenCity/GroupIconImage";
import IconWithBackground from "../components/GreenCity/IconWithBackground";
import TextButton from "../components/GreenCity/TextButton";
import Separator from "../components/Separator";
import MessageModal from "../components/Models/GreenCity/MessageModal";
import { ErrorMessageModal } from "../components/ErrorMessageModal";
import UpdateInput from "../components/Models/UpdateInput";
import MebukuIdUpgradeBanner from "../components/MebukuIdUpgradeBanner";
import IconButtonArrow from "../components/GreenCity/IconButtonArrow";
import Divider from "@mui/material/Divider";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import KeyboardArrowUpOutlinedIcon from "@mui/icons-material/KeyboardArrowUpOutlined";
import { Box, colors } from "@mui/material";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ChatIcon from "@mui/icons-material/Chat";
import AddLinkIcon from "@mui/icons-material/AddLink";
import QrCode2Icon from '@mui/icons-material/QrCode2';
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import BorderColorIcon from "@mui/icons-material/BorderColorOutlined";
import { styles } from "./styles";
import { colors as farm_colors } from "../common/colors";
import "bootstrap/dist/css/bootstrap.min.css";
import "../App.css";
import profile from "../assets/images/profile_img.png";
import SafeTextWithLinks from "../components/SafeTextWithLinks";

interface EventDate {
  title: string;
  color: string;
  start: string;
  end: string;
  id: number | string;
  post_id?: number | string;
}

interface PersonalPlan {
  title: string;
  color: string;
  start: string;
  end: string;
  id: number | string;
  type: "personal_plan";
  user_id: number | string;
}

type CombinedDate = EventDate | PersonalPlan;

export default function MyFarm() {
  const dispatch = useAppDispatch();
  const { user } = useSelector((state: any) => state.userAuth);
  const [memberGroups, setMemberGroups] = useState<any[]>([]);
  const [visible, setVisible] = useState<boolean>(false);
  const [dates, setDates] = useState<CombinedDate[]>([]);
  const [personalPlan, setPersonalPlan] = useState<any>(null);
  const [showDeniedMessage, setDeniedMessage] = useState<any>();
  const [showDescriptionUpdate, setShowDescriptionUpdate] = useState<boolean>(false);
  const [openErrorMessageModal, setOpenErrorMessageModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<boolean>(false);
  const navigate = useNavigate();

  const AddIcon = () => {
    return (
      <div style={style.addIcon}>
        <AddRoundedIcon sx={{ width: 20, height: 20 }} />
      </div>
    );
  };

  const getUserEvents = async (user_id: number) => {
    try {
      const res = await appServices.userEvents(user_id);
      const leaderPlans = await appServices.getPersonalPlans(user_id);
      const eventPost = await appServices.getMemberGroupFreeEvents();
      const eventDates: EventDate[] =
        res?.post_event_users
          ?.filter((event: any) => event?.status !== "Cancelled") // Exclude events with status "Cancelled"
          .map((event: any) => ({
            title: event?.event_title,
            color: event?.status === "applied" ? "#86BB57" : "gray",
            start: event?.event_start_date,
            end: dayjs(event?.event_end_date).add(1, "day").format("YYYY-MM-DD"),
            id: event?.id,
            post_id: event?.post_id,
          })) || []; // Default to empty array if undefined

      const personalPlans: PersonalPlan[] =
        leaderPlans?.personal_plans?.map((plan: any) => ({
          title: plan?.title,
          color: "#3a79f0",
          start: plan?.start_date,
          end: dayjs(plan?.end_date).add(1, "day").format("YYYY-MM-DD"),
          id: plan?.id,
          type: "personal_plan",
          user_id: plan?.user_id,
        })) || []; // Default to empty array if undefined

      const memberGroupFreeEvents: EventDate[] =
        eventPost.post_events
          ?.map((event: any) => ({
            title: event?.title,
            color: "#86BB57",
            start: event?.start_date,
            end: dayjs(event?.end_date).add(1, "day").format("YYYY-MM-DD"),
            id: event?.id,
            post_id: event?.post_id,
         })) || []; // Default to empty array if undefined

      // Concatenate both arrays and set in setDates hook
      const combinedDates: CombinedDate[] = [...eventDates, ...personalPlans, ...memberGroupFreeEvents];
      setDates(combinedDates);
    } catch (error) {
      console.error(error);
    }
  };

  const getSelectedEvent = async (date: any) => {
    try {
      if (date?.type === "personal_plan") {
        setVisible(true);
        const res = await appServices.getSinglePersonalPlan(date.id);
        setPersonalPlan(res?.personal_plan);
        setVisible(false);
      } else {
        const res = await appServices.getSingleEventById(date.post_id);
        navigate("/calenderevent", { state: { event: [res?.post] } });
      }
    } catch (error) {
      setVisible(false);
      console.error(error);
    }
  };

  useEffect(() => {
    if (user?.id) apiCalls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const hasMemberGroup = () => {
    return memberGroups.some((group: any) => group.owner.id === user.id)
  }

  const apiCalls = async () => {
    if (!isRegularAppUser(user)) return

    setVisible(true);
    try {
      getUserEvents(user.id);
      const params = {status: ['approved', 'denied', 'invited', 'unpaid_approved']};
      const res = await appServices.getMemberGroups(params);
      setMemberGroups(res.member_groups)
    } catch (error: any) {
      if (error?.response?.status === 403) {
        setAuthorizationToken("");
        navigate("/");
      }
    } finally {
      setVisible(false);
    }
  };

  const handleGroup = (group: any) => {
    dispatch(clearGroupInfo());
    if (isGroupLeader(group, user)) {
      navigate("/groupleader", {
        state: {
          groupId: group.id,
        },
      });
    } else {
      if (group?.status === "unpaid_approved") {
        navigate("/grouppayment", { state: { groupInfo: group } });
      } else if (group?.status === "approved") {
        navigate("/groupleader", {
          state: {
            groupId: group.id,
          },
        });
      } else if (group?.status === "denied") {
        setDeniedMessage(group);
      } else if (group?.status === "invited") {
        navigateGroupTop(group)
      } else {
        navigateGroupTop(group)
      }
    }
  };
  const handleDeleteMember = async (id: any) => {
    try {
      await appServices.deleteMember(id);
      const params = {status: ['approved', 'denied', 'invited', 'unpaid_approved']};
      const res = await appServices.getMemberGroups(params);
      setMemberGroups(res.member_groups)
    } catch (error) {
    }
  };

  const handleDenied = async () => {
    setVisible(true);
    try {
      const res = await appServices.findMember(user?.id, showDeniedMessage?.id);
      await handleDeleteMember(res?.member_group_user?.id);
      setVisible(false);
    } catch (error) {
      setVisible(false);
    }
  };

  const handleUpdateDescription = async (text: string) => {
    setShowDescriptionUpdate(false);
    setVisible(true);
    try {
      const res = await authService.updateAppUser(
        user?.id, {description: text,}
      )
      dispatch(updateUser(res.data.user));
      return true
    } catch(error: any) {
      console.error(error)
      setErrorMessage(error?.response?.data?.message ?? T("api_unknown_error"));
      setOpenErrorMessageModal(true)
      return false
    } finally {
      setVisible(false)
    };
  };

  const handleCreateMemberGroup = async () => {
    try {
      const res = await appServices.canCreateMemberGroup()
      if (res.can_create_member_group) {
        dispatch(clearGroupInfo());
        navigate("/disclosure");
      } else {
        setOpenErrorMessageModal(true)
        setErrorMessage([T("has_already_member_group")])
      }
    } catch (error) {
      setOpenErrorMessageModal(true)
      setErrorMessage([T("api_unknown_error")])
    }
  }

  const closeErrorMessageModal = () => {
    setOpenErrorMessageModal(false)
    setErrorMessage([])
  }

  const navigateGroupTop = (groupInfo: any) => {
    navigate("/groupinforeview", {state: {groupId: groupInfo?.id}});
  };

  const handleClickEditDescription = () => {
    if (!isRegularAppUser(user)) return
    setShowDescriptionUpdate(true)
  }

  const closeMessageModal = () => {
    setDeniedMessage(undefined);
    handleDenied();
  }

  return (
    <Fragment>
      <div style={{ ...styles.farmContainer, padding: 0, marginTop: 30 }}>
        <LoadingModal visible={visible} />
        <div style={{ ...styles.farmProfileInfo, flexDirection: "column" }}>
          <img src={user.image !== null ? convertUserImage(user.image) : profile} alt={"userimg"} style={styles.farmProfileIcon} />

          <span style={styles.farmInfoTitle}>{user.nickname}</span>
          <Box
            onClick={() => handleClickEditDescription()}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: !isRegularAppUser(user) ? 0.5 : 1,
            }}
          >
            <Typography>{user?.description || T("mypage_default_description")}</Typography>
            <BorderColorIcon
              sx={{ ml: 0.5, height: 18, width: 18 }}
              style={{
                color: colors.grey[400],
              }}
            />
          </Box>
        </div>

        <div style={style.leaderEvent}>
          <TextButton disabled={!isRegularAppUser(user)} onClick={() => navigate("/createleadereventpost")} Icon={AddIcon} title={T("mypage_add_personal_plan")} />
        </div>
        <div>
          <EventCalendar getSelectedEvent={getSelectedEvent} dates={dates} />
        </div>
        {personalPlan && (
          <Box>
            <Box sx={style.planHeader}>{personalPlan?.title}</Box>
            <Box sx={{ padding: "5px 10px" }}>
              <Typography sx={{ color: "#A2A2A2", fontSize: 12 }}>
                {TF("{0}:", T("personal_plan_date"))}
                {dayjs(personalPlan?.start_date).format("YYYY-MM-DD (dddd)")}
              </Typography>
              <Typography>{personalPlan?.description}</Typography>
              <Box sx={{ marginTop: 2 }}>
                <Typography sx={{ textAlign: "left" }}>{T("mypage_personal_plan_website")}</Typography>
                <Separator color={farm_colors.farm_green500} sx={{ height: "2px" }} />
                <div>
                  <SafeTextWithLinks text={personalPlan?.web_url} />
                </div>
              </Box>
              <Typography
                onClick={() =>
                  navigate("/createleadereventpost", {
                    state: { personalPlan },
                  })
                }
                sx={{ marginTop: 3, color: "#A2A2A2", fontSize: 12 }}
              >
                {T("correct")}
              </Typography>
            </Box>
          </Box>
        )}
        <div style={styles.portionBg}>
          <p style={{ fontSize: 14, fontWeight: 600 }}>
            <BorderColorOutlinedIcon /> {T("mypage_opinion_post")}
          </p>

          <ButtonArrow disabled={!isRegularAppUser(user)} title={T("private_post")} onClick={() => navigate("/posthistory")} />

          <ButtonArrow disabled={!isRegularAppUser(user)} title={T("comment_history")} onClick={() => navigate("/commenthistory")} />

          <ButtonArrow disabled={!isRegularAppUser(user)} title={T("everyones_post_history")} onClick={() => navigate("/everyoneshistory")} />
        </div>

        <div style={styles.portionBg}>
          <p style={{ fontSize: 14, fontWeight: 600 }}>
            <ChatIcon /> {T("chat")}
          </p>

          <ButtonArrow disabled={!isRegularAppUser(user)} title={T("chat_list")}onClick={() => navigate("/userchatlist")} />
        </div>

        <div style={styles.portionBg}>
          <p style={{ fontSize: 14, fontWeight: 600 }}>
            <PeopleAltOutlinedIcon /> {T("group")}
          </p>
          <Box
            style={{
              display: "flex", // Flexbox to align items in one row
              flexDirection: "row", // Horizontal row
              gap: 6, // Optional: gap between items
            }}
          >
            <IconButtonArrow
              disabled={!isRegularAppUser(user) || hasMemberGroup()}
              icon={<IconWithBackground backgroundColor={farm_colors.farm_green500} icon={<SentimentSatisfiedAltIcon style={{ color: "white", fontSize: 20 }} />} />}
              title={T("mypage_create_member_group")}
              onClick={() => {
                handleCreateMemberGroup()
              }}
            />
            <IconButtonArrow
              icon={<IconWithBackground backgroundColor={farm_colors.farm_green500} icon={<SearchIcon style={{ color: "white", fontSize: 20 }} />} />}
              title={T("mypage_search")}
              onClick={() => navigate("/search")}
            />
          </Box>
          <IconButtonArrow
            disabled={!isRegularAppUser(user)}
            icon={<IconWithBackground backgroundColor={farm_colors.farm_blue200} icon={<AddLinkIcon style={{ color: "white", fontSize: 20 }} />} />}
            title={T("mypage_join_member_group_by_invitation_link")}
            onClick={() => {
              navigate("/groupinvitation");
            }}
          />
          <IconButtonArrow
            disabled={!isRegularAppUser(user)}
            icon={<IconWithBackground backgroundColor={farm_colors.farm_blue200} icon={<QrCode2Icon style={{ color: "white", fontSize: 20 }} />} />}
            title={T("mypage_join_member_group_by_qr_code")}
            onClick={() => {
              navigate("/scangroupqr");
            }}
          />

          {/* groups list */}
          <Box display="flex" flexDirection="column">
            {memberGroups?.map((group: any, index: number) => (
              <IconButtonArrow
                key={index} // Use a unique key in a real app
                icon={<GroupIconImage groupId={group?.id} alt="" style={{ borderRadius: 13, height: 26, width: 26 }} />}
                title={group?.name || ""} // Title from the group
                role={
                  isGroupLeader(group, user)
                    ? getRole("leader")
                    : group?.status === "invited"
                    ? T("invited")
                    : group?.status === "unpaid_approved"
                    ? T("payment_pending")
                    : getRole(group?.role)
                } // Title from the group
                onClick={() => handleGroup(group)}
              />
            ))}
          </Box>
        </div>

        <div style={styles.portionBg}>
          <p style={{ fontSize: 14, fontWeight: 600 }}>
            <span
              style={{
                fontSize: 18,
                border: 1,
                borderRadius: 18,
                borderStyle: "solid",
                paddingLeft: 5,
                paddingRight: 5,
                width: 10,
                height: 10,
              }}
            >
              &#165;
            </span>{" "}
            {T("payment")}
          </p>

          <ButtonArrow
            disabled={!isRegularAppUser(user)}
            title={T("mypage_pay_to_group")}
            onClick={() => {
              navigate("/grouppayment");
            }}
          />

          <ButtonArrow
            disabled={!isRegularAppUser(user)}
            title={T("reimbursement")}
            onClick={() => {
              navigate("/paymentreimbursement");
            }}
          />

          <ButtonArrow disabled={!isRegularAppUser(user)} title={T("payment_history")} onClick={() => navigate("/paymenthistory")} />

          <ButtonArrow
            disabled={!isRegularAppUser(user)}
            title={T("mypage_reimbursement_history")}
            onClick={() => {
              navigate("/reimbursements");
            }}
          />
        </div>

        <div style={styles.portionBg}>
          <p style={{ fontSize: 14, fontWeight: 600 }}>
            <SettingsOutlinedIcon /> {T("other")}
          </p>

          <Accordion
            className=""
            style={{
              boxShadow: "none",
              marginLeft: 16,
              marginRight: 16,
              borderRadius: 8,
              marginBottom: 14,
              height: expanded ? 100 : 40,
              paddingTop: -4,
              backgroundColor: farm_colors.farm_white
            }}
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
          >
            <AccordionSummary
              sx={{ color: "black", minHeight: 40, height: 40 }}
              expandIcon={
                expanded ? (
                  <KeyboardArrowUpOutlinedIcon fontSize={"large"} style={{ color: "black", fontSize: 22 }} />
                ) : (
                  <ArrowForwardIosIcon style={{ color: "black", fontSize: 18 }} />
                )
              }
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography style={{ fontSize: 14 }}>{T("mypage_questionnaire")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <p
                  onClick={(event) => handleClickUrl(event)}
                >
                  <a style={{ fontSize: 14 }} href="https://forms.office.com/r/7fnxwKXWRW">
                    https://forms.office.com/r/7fnxwKXWRW
                  </a>
                </p>
                <Divider style={styles.farmDivider3} />
              </div>
            </AccordionDetails>
          </Accordion>

          <ButtonArrow title={T("dashboard")} onClick={() => navigate("/dashboard")} />
          <MessageModal
            showButton
            open={showDeniedMessage?.id ? true : false}
            buttonText={T("close")}
            message={T("mypage_join_member_group_rejected")}
            onClose={() => closeMessageModal()}
            onContinue={() => {}}
            buttonStyles={style.messageModalButton}
          />
          <ErrorMessageModal
            open={openErrorMessageModal}
            messages={errorMessage}
            onClose={() => closeErrorMessageModal()}
          />
        </div>
      </div>
      <UpdateInput
        onClose={() => setShowDescriptionUpdate(false)}
        onContinue={handleUpdateDescription}
        lable={T("mypage_description_label")}
        oldDescriptions={user?.description}
        open={showDescriptionUpdate}
        message={T("mypage_description_message")}
      />
      <MebukuIdUpgradeBanner />
    </Fragment>
  );
}

const style = {
  addIcon: {
    border: "2px solid",
    borderRadius: 5,
    borderColor: "#000000",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  leaderEvent: {
    width: "fit-content",
    marginLeft: "auto",
    marginRight: "10px",
  },
  planHeader: {
    width: "100%",
    backgroundColor: "#E3F0FF",
    padding: "5px 10px",
    fontWeight: 600,
  },
  messageModalButton: {
    color: "#4792E0",
    border: "2px solid ",
    margin: "0px 30px",
    borderRadius: "10px",
    padding: "10px",
    width: "85%",
    maxWidth: 300,
  }
};
